import { createSelector } from '@reduxjs/toolkit';

import type { AssetsGetShareSchemaWithIdNullable } from '@iconik-io/iconik-api/src/services/assets';
import type {
  FilesFileSchemaWithId,
  FilesProxySchemaWithId,
} from '@iconik-io/iconik-api/src/services/files';
import { OBJECT_TYPE_ASSETS } from '@iconik-io/iconik-api/src/types/object_types';
import type { AssetEntityNormalized } from '@iconik-io/shared-lib/src/redux/entities/normalized/asset';
import { getProxyMediaType } from '@iconik-io/shared-lib/src/utils/get_proxy_media_type';
import { seekableMediaTypes } from '@iconik-io/shared-lib/src/utils/media_type';

import { ASSET_METADATA_KEY } from '../../../../constants/data';
import {
  selectAssetFiles,
  selectAssetProxy,
} from '../../../../ducks/pages/asset/selectors';
import type { AppState } from '../../../../store/types';
import type { PermissionValues } from '../../../../types/common';
import { selectUserRoles } from '../../../../utils/get_user_roles';
import {
  getCommentsPanelItem,
  getFilesPanelItem,
  getMetadataPanelItem,
  getTranscriptionsPanelItem,
  reviewAndApprovePanelItem,
} from '../common';

const getShare = (state: AppState): AssetsGetShareSchemaWithIdNullable =>
  state.share.share;

const getEntityId = (
  _state: AppState,
  _panels: unknown,
  _dispatch: unknown,
  entityId: string | undefined,
) => entityId;

const getAsset = (
  state: AppState,
  _panels: unknown,
  _dispatch: unknown,
  entityId: string | undefined,
): AssetEntityNormalized | undefined =>
  entityId ? state.entities.assets[entityId] : undefined;

const getAssetProxy = (
  state: AppState,
  _panels: unknown,
  _dispatch: unknown,
  entityId: string | undefined,
  versionId: string | undefined,
): FilesProxySchemaWithId | undefined =>
  selectAssetProxy(state, entityId, versionId);

const getAssetFiles = (
  state: AppState,
  _panels: unknown,
  _dispatch: unknown,
  entityId: string | undefined,
  versionId: string | undefined,
): FilesFileSchemaWithId[] => selectAssetFiles(state, entityId, versionId);

export default createSelector(
  selectUserRoles,
  getShare,
  getAsset,
  getAssetProxy,
  getAssetFiles,
  getEntityId,
  (userRoles, share, asset, assetProxy, assetFiles, entityId) => {
    const panels = [];

    if (!asset || !asset.status || asset.status === 'DELETED') {
      return [];
    }
    const isSubclipAssetType = asset.type === 'SUBCLIP';

    const metadataViews = share.metadata_views;
    if (metadataViews && metadataViews.length) {
      const entityPermissions: PermissionValues = {
        read: true,
        write: false,
        delete: false,
        'change-acl': false,
      };
      panels.push(
        getMetadataPanelItem(
          OBJECT_TYPE_ASSETS,
          ASSET_METADATA_KEY,
          asset?.category ?? undefined,
          false,
          true,
          entityPermissions,
          share,
        ),
      );
    }

    if (userRoles.canReadSegments) {
      if (share.allow_comments || share.allow_approving_comments) {
        panels.push(getCommentsPanelItem(true, true));
      }

      const proxyMediaType = assetProxy
        ? getProxyMediaType(assetProxy)
        : 'file';
      const seekableMediaType = seekableMediaTypes.has(proxyMediaType);
      if (seekableMediaType && userRoles.canReadTranscriptions) {
        panels.push(getTranscriptionsPanelItem(entityId, false));
      }
    }

    if (userRoles.canReadApprovalRequest) {
      panels.push(reviewAndApprovePanelItem(false));
    }
    const shouldShowFilesPanelForSubclip =
      isSubclipAssetType && assetFiles.length > 0;

    const shouldShowFilesPanel =
      !(asset?.type === 'PLACEHOLDER') &&
      !(asset?.type === 'LINK') &&
      !isSubclipAssetType;
    if (
      userRoles.canReadFormats &&
      userRoles.canReadFiles &&
      (shouldShowFilesPanel || shouldShowFilesPanelForSubclip)
    ) {
      panels.push(getFilesPanelItem(false));
    }

    return panels;
  },
);
